import { useEffect, useState, useCallback, useMemo, memo } from "react";
import { Alert, Pagination, Spin, Table, Tooltip } from "antd";
import { EyeOutlined, HomeOutlined, UserOutlined, CalendarOutlined, TeamOutlined, MailOutlined } from "@ant-design/icons";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

import service from "../../../auth/FetchInterceptor";
import PrefixPath from "../../../config/AppConfig";
import MuiTable from "../../../Components/TableMui/MuiTable";
import "./SMSList.scss";
import { formatDate } from "../../../utils/Formatters";

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const update = () => setIsMobile(window.innerWidth <= 750);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return isMobile;
};

const SORT_FIELDS = [
  { value: "companyName", label: "Property" },
  { value: "senderName", label: "Sender Name" },
  { value: "dateCreated", label: "Date Sent" },
  { value: "numberOfRecipients", label: "Recipients" },
];

const parseMessageParts = (text, companyName) => {
  if (!text) return { header: "", body: "", footer: "" };
  let remaining = text.replace(/\r\n/g, "\n");
  let header = "";
  let footer = "";

  if (companyName) {
    const headerPrefix = companyName + "\n";
    if (remaining.startsWith(headerPrefix)) {
      header = companyName;
      remaining = remaining.slice(headerPrefix.length);
    }
  }

  const dashFooterIdx = remaining.indexOf("\n---");
  if (dashFooterIdx !== -1) {
    footer = remaining.slice(dashFooterIdx + 1).replace(/^---\n?/, "").trim();
    remaining = remaining.slice(0, dashFooterIdx).trim();
  } else {
    const replyIdx = remaining.indexOf("Reply STOP to unsubscribe");
    if (replyIdx !== -1) {
      footer = remaining.slice(replyIdx).trim();
      remaining = remaining.slice(0, replyIdx).trim();
    }
  }

  return { header, body: remaining.trim(), footer };
};

const SMSMobileCard = memo(({ row, onClick }) => {
  const { body } = parseMessageParts(row.messageContent, row.companyName);

  return (
    <div
      className="sms-mobile-card"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      {/* Property header */}
      <div className="sms-mobile-card__header">
        <div className="sms-mobile-card__icon-wrap">
          <HomeOutlined style={{ color: "#1677ff", fontSize: 17 }} />
        </div>
        <div className="sms-mobile-card__header-text">
          <div className="sms-mobile-card__label">Property</div>
          <div className="sms-mobile-card__title">{row.companyName || "-"}</div>
        </div>
      </div>

      <div className="sms-mobile-card__body">
        {/* Sender + Date Sent */}
        <div className="sms-mobile-card__row">
          <div className="sms-mobile-card__field">
            <div className="sms-mobile-card__icon-wrap sms-mobile-card__icon-wrap--sm">
              <UserOutlined style={{ color: "#1677ff", fontSize: 13 }} />
            </div>
            <div>
              <div className="sms-mobile-card__label">Sender</div>
              <div className="sms-mobile-card__value">{row.senderName || "-"}</div>
            </div>
          </div>
          <div className="sms-mobile-card__field">
            <div className="sms-mobile-card__icon-wrap sms-mobile-card__icon-wrap--sm">
              <CalendarOutlined style={{ color: "#1677ff", fontSize: 13 }} />
            </div>
            <div>
              <div className="sms-mobile-card__label">Date Sent</div>
              <div className="sms-mobile-card__value">{formatDate(row.dateCreated)}</div>
            </div>
          </div>
        </div>

        {/* Recipients */}
        <div className="sms-mobile-card__row">
          <div className="sms-mobile-card__field">
            <div className="sms-mobile-card__icon-wrap sms-mobile-card__icon-wrap--sm">
              <TeamOutlined style={{ color: "#1677ff", fontSize: 13 }} />
            </div>
            <div>
              <div className="sms-mobile-card__label">Recipients</div>
              <div className="sms-mobile-card__value">{row.numberOfRecipients ?? "-"}</div>
            </div>
          </div>
        </div>

        {/* Message Preview */}
        <div className="sms-mobile-card__message">
          <div className="sms-mobile-card__message-label">
            <div className="sms-mobile-card__icon-wrap sms-mobile-card__icon-wrap--sm">
              <MailOutlined style={{ color: "#1677ff", fontSize: 13 }} />
            </div>
            <span className="sms-mobile-card__label">Message Preview</span>
          </div>
          <div className="sms-mobile-card__message-box">
            {body ? (
              <div className="sms-mobile-card__msg-part">
                <span>{body || "-"}</span>
              </div>
            ) : (
              <div className="sms-mobile-card__msg-part">
                <span style={{ color: "#9ca3af" }}>-</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

SMSMobileCard.displayName = "SMSMobileCard";

const DesktopView = memo(({ groupedRows, propertyColumns, getDetailPanelContent }) => (
  <div className="sms-list__desktop">
    <MuiTable
      rows={groupedRows}
      columnOrder={propertyColumns}
      getRowId={(row) => row.id}
      getDetailPanelContent={getDetailPanelContent}
    />
  </div>
));

DesktopView.displayName = "SMSListDesktopView";

const MobileView = memo(
  ({
    rows,
    pagination,
    currentPage,
    pageSize,
    handleCustomPageChange,
    handlePageSize,
    handleSorting,
    sortModel,
    onRowClick,
  }) => {
    const sortField = sortModel?.[0]?.field || "";
    const sortDir = sortModel?.[0]?.sort || "asc";

    const handleSortFieldChange = (e) => {
      const field = e.target.value;
      handleSorting(field ? [{ field, sort: sortDir || "asc" }] : []);
    };

    const handleSortDirChange = (e) => {
      if (!sortField) return;
      handleSorting([{ field: sortField, sort: e.target.value }]);
    };

    return (
      <Box className="sms-list__mobile">
        <div className="sms-list__mobile-filters">
          <div className="sms-list__sort-panel-grid">
            <FormControl size="small" fullWidth className="sms-list__sort-control">
              <InputLabel id="sms-sort-field">Sort by</InputLabel>
              <Select
                labelId="sms-sort-field"
                label="Sort by"
                value={sortField}
                onChange={handleSortFieldChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {SORT_FIELDS.map((f) => (
                  <MenuItem key={f.value} value={f.value}>
                    {f.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth disabled={!sortField} className="sms-list__sort-control">
              <InputLabel id="sms-sort-dir">Direction</InputLabel>
              <Select
                labelId="sms-sort-dir"
                label="Direction"
                value={sortDir}
                onChange={handleSortDirChange}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>

        {!rows?.length ? (
          <div className="sms-list__empty">No data found.</div>
        ) : (
          <div className="table-card-stack">
            {rows.map((row) => (
              <SMSMobileCard
                key={row.messageKey}
                row={row}
                onClick={() => onRowClick?.(row)}
              />
            ))}
          </div>
        )}

        <div className="sms-list__pagination">
          <span className="sms-list__pagination-text">
            Showing {Math.min((currentPage - 1) * pageSize + 1, pagination.total)}–
            {Math.min(currentPage * pageSize, pagination.total)} of {pagination.total} rows
          </span>

          <div className="sms-list__pagination-size">
            <select
              value={pageSize}
              onChange={handlePageSize}
              className="sms-list__page-size-select"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={pagination.total}
            onChange={handleCustomPageChange}
            showSizeChanger={false}
            showLessItems
          />
        </div>
      </Box>
    );
  }
);

MobileView.displayName = "SMSListMobileView";

const SMSList = ({ searchSender: externalSearch = "" }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [sortModel, setSortModel] = useState([{ field: "dateCreated", sort: "desc" }]);
  const [searchValue, setSearchValue] = useState(externalSearch);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    setSearchValue(externalSearch);
    setCurrentPage(1);
  }, [externalSearch]);

  useEffect(() => {
    setLoading(true);
    setError(null);

    service
      .get(PrefixPath.SMS_API_BASE_URL + "Message")
      .then((res) => {
        if (res && (res.isSuccess || res.IsSuccess)) {
          setData(res.value || []);
        } else {
          setError(res?.message || res?.Message || "Failed to load data.");
        }
      })
      .catch((err) => setError(err?.message || "Error fetching data."))
      .finally(() => setLoading(false));
  }, []);

  // Filtered rows (no sort) — used for desktop grouped view
  const filteredRows = useMemo(() => {
    if (!searchValue) return data;
    const needle = searchValue.toLowerCase();
    return data.filter(
      (item) =>
        (item.sender || "").toLowerCase().includes(needle) ||
        (item.senderName || "").toLowerCase().includes(needle) ||
        (item.companyName || "").toLowerCase().includes(needle) ||
        formatDate(item.dateCreated).toLowerCase().includes(needle)
    );
  }, [data, searchValue]);

  // One row per property — used as parent rows in the desktop grid
  const groupedRows = useMemo(() => {
    const groups = {};
    filteredRows.forEach((msg) => {
      const key = msg.companyName || "(No Property)";
      if (!groups[key]) {
        groups[key] = {
          id: `property-${key}`,
          companyName: key,
          messages: [],
          latestDate: null,
        };
      }
      groups[key].messages.push(msg);
      if (
        !groups[key].latestDate ||
        new Date(msg.dateCreated) > new Date(groups[key].latestDate)
      ) {
        groups[key].latestDate = msg.dateCreated;
      }
    });
    return Object.values(groups);
  }, [filteredRows]);

  // Parent-level columns (one row per property)
  const propertyColumns = useMemo(
    () => [
      { field: "companyName", 
        headerName: "Property", 
        flex: 1,
        width: "34%", 
        minWidth: 150 },
      {
        field: "messageCount",
        headerName: "Messages",
        flex: 1,
        width: "33%",
        align: "center",
        headerAlign: "center",
        valueGetter: (_, row) => row.messages?.length ?? 0,
      },
      {
        field: "latestDate",
        headerName: "Latest Message",
        flex: 1,
        width: "33%",
        minWidth: 150,
        renderCell: (params) => formatDate(params.row.latestDate),
      },
    ],
    []
  );

  // Inner columns for the detail panel (Ant Design Table format)
  const innerColumns = useMemo(
    () => [
      {
        title: "Sender Name",
        dataIndex: "senderName",
        key: "senderName",
        width: "15%",
        render: (v) => v || "-",
        sorter: (a, b) => (a.senderName || "").localeCompare(b.senderName || ""),
      },
      {
        title: "Date Sent",
        dataIndex: "dateCreated",
        key: "dateCreated",
        width: "15%",
        render: formatDate,
        sorter: (a, b) => new Date(a.dateCreated) - new Date(b.dateCreated),
        defaultSortOrder: "descend",
      },
      {
        title: "Recipients",
        dataIndex: "numberOfRecipients",
        key: "numberOfRecipients",
        width: "15%",
        align: "center",
        sorter: (a, b) => (a.numberOfRecipients ?? 0) - (b.numberOfRecipients ?? 0),
      },
      {
        title: "Message Preview",
        dataIndex: "messageContent",
        key: "messageContent",
        width: "30%",
        ellipsis: { showTitle: false },
        render: (text, record) => {
          if (!text) return "-";
          let body = text;
          // Strip header (PropertyName\n)
          if (record.companyName) {
            const header = record.companyName + "\n";
            if (body.startsWith(header)) body = body.slice(header.length);
          }
          // Strip footer (legacy "\n--- ..." or current "Reply STOP ...")
          let footerIdx = body.indexOf("\n---");
          if (footerIdx === -1) footerIdx = body.indexOf("Reply STOP to unsubscribe");
          if (footerIdx !== -1) body = body.slice(0, footerIdx);
          const preview = body.trim();
          return preview ? (
            <Tooltip title={preview} placement="topLeft">
              <span className="sms-list__message-ellipsis">{preview}</span>
            </Tooltip>
          ) : "-";
        },
      },
      {
        title: "",
        key: "actions",
        width: "10%",
        align: "center",
        render: (_, record) => (
          <EyeOutlined
            data-testid={`sms-view-btn-${record.id}`}
            className="sms-list__eye-icon"
            title="View SMS Details"
            onClick={(e) => {
              e.stopPropagation();
              if (record.id) navigate(`/AAM/sms/${record.id}`);
            }}
          />
        ),
      },
    ],
    [navigate]
  );

  // Detail panel rendered below each property row when expanded
  const getDetailPanelContent = useCallback(
    ({ row }) => (
      <Box className="sms-list__detail-panel">
        <Table
          rowKey={(r) => r.id || r.messageKey}
          columns={innerColumns}
          dataSource={row.messages}
          pagination={false}
          size="middle"
          tableLayout="fixed"
          className="sms-list__inner-table"
          onRow={(record) => ({
            onClick: () => {
              if (record?.id) navigate(`/AAM/sms/${record.id}`);
            },
            style: { cursor: "pointer" },
          })}
        />
      </Box>
    ),
    [innerColumns, navigate]
  );

  // Filtered + sorted manually — used only on mobile
  const filteredAndSortedRows = useMemo(() => {
    const rows = [...filteredRows];
    const first = sortModel?.[0];
    if (first?.field && first?.sort) {
      const dir = first.sort === "asc" ? 1 : -1;
      rows.sort((a, b) => {
        let av = a[first.field];
        let bv = b[first.field];

        if (first.field === "dateCreated") {
          av = av ? new Date(av).getTime() : 0;
          bv = bv ? new Date(bv).getTime() : 0;
        }

        if (av == null) return -1 * dir;
        if (bv == null) return 1 * dir;
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }
    return rows;
  }, [filteredRows, sortModel]);

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAndSortedRows.slice(start, start + pageSize);
  }, [filteredAndSortedRows, currentPage, pageSize]);

  const tablePagination = useMemo(
    () => ({ total: filteredRows.length }),
    [filteredRows.length]
  );

  const handleSorting = useCallback((next) => {
    setSortModel(next || []);
    setCurrentPage(1);
  }, []);

  const handleCustomPageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handlePageSize = useCallback((e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  }, []);

  const handleRowClick = useCallback(
    (record) => {
      if (record?.id) navigate(`/AAM/sms/${record.id}`);
    },
    [navigate]
  );

  if (error) {
    return <Alert type="error" message={error} />;
  }

  return (
    <Spin spinning={loading} size="large">
      <div className="sms-list">
        {isMobile ? (
          <MobileView
            rows={pagedRows}
            pagination={tablePagination}
            currentPage={currentPage}
            pageSize={pageSize}
            handleCustomPageChange={handleCustomPageChange}
            handlePageSize={handlePageSize}
            handleSorting={handleSorting}
            sortModel={sortModel}
            onRowClick={handleRowClick}
          />
        ) : (
          <DesktopView
            groupedRows={groupedRows}
            propertyColumns={propertyColumns}
            getDetailPanelContent={getDetailPanelContent}
          />
        )}
      </div>
    </Spin>
  );
};

SMSList.propTypes = {
  searchSender: PropTypes.string,
};

export default SMSList;